import React, { useContext, useEffect, useState } from "react"
import { StateMachineProvider, StateMachineStepView } from "../../context/stateMachine/StateMachineV2"
import { partnerRegistrationSection } from "./workflow"
import { useBodyClass } from "../../../utils/Hooks"
import CarrotClient from "lib/carrot-api"
import Settings from "utils/CarrotConfig"
import { useLDClient } from "launchdarkly-react-client-sdk"
import { useIntlMessages } from "components/views/app/IntlMessages"
import { LoadingInner } from "components/views/Loading"

export const PartnerRegistrationFlowContext = React.createContext<PartnerRegistrationFlowContextProps>(null)

export const usePartnerRegistrationFlow = () => useContext(PartnerRegistrationFlowContext)
const client = new CarrotClient(Settings.CARROT_BACKEND_URL)

export default function PartnerRegistrationFlow(props): JSX.Element {
  useBodyClass("bg-warm-gray")
  const ldClient = useLDClient()

  const guid = props.match.params.guid
  const [password, setPassword] = useState(null)
  const [readPolicy, setReadPolicy] = useState(false)
  const [readTerms, setReadTerms] = useState(false)
  const [dataConsent, setDataConsent] = useState(false)
  const [emailOptIn, setEmailOptIn] = useState(false)

  const [email, setEmail] = useState(null)
  const [companyId, setCompanyId] = useState(0)
  const { didCompiledMessagesLoad } = useIntlMessages()
  const [showPage, setShowPage] = useState(false)

  const context = {
    guid,
    password,
    setPassword,
    readPolicy,
    setReadPolicy,
    readTerms,
    setReadTerms,
    dataConsent,
    setDataConsent,
    emailOptIn,
    setEmailOptIn
  }

  useEffect(() => {
    async function fetchRegisteringUser(): Promise<void> {
      await client.logout()
      const guidFromParams = props.match.params.guid
      const registeringPartner = await client.getRegisteringPartner(guidFromParams)
      if (!registeringPartner) {
        return
      }
      setEmail(registeringPartner.email)
      setCompanyId(registeringPartner.companyId)
    }

    fetchRegisteringUser().catch((error) => reportError(error))
  }, [props.match.params.guid])

  useEffect(() => {
    async function loadLDFlagsForRegisteringPartner(memberEmail, memberCompanyId): Promise<void> {
      if (ldClient && memberEmail && memberCompanyId) {
        await ldClient.waitUntilReady()

        const ldUser = {
          key: memberEmail,
          email: memberEmail,
          custom: { companyId: memberCompanyId }
        }

        await ldClient.identify(ldUser)
      }
    }

    loadLDFlagsForRegisteringPartner(email, companyId).catch((error) => reportError(error))
  }, [ldClient, email, companyId])

  useEffect(() => {
    if (didCompiledMessagesLoad) {
      setShowPage(true)
    }
  }, [didCompiledMessagesLoad])

  if (!showPage) {
    return <LoadingInner />
  }
  return (
    <PartnerRegistrationFlowContext.Provider value={context}>
      <StateMachineProvider
        initialSection={partnerRegistrationSection.name}
        stateMachineDescription={[partnerRegistrationSection]}
        onComplete={null}
      >
        <StateMachineStepView />
      </StateMachineProvider>
    </PartnerRegistrationFlowContext.Provider>
  )
}

export type PartnerRegistrationFlowContextProps = {
  guid: string
  password: string
  setPassword: (password: string) => void
  readPolicy: boolean
  setReadPolicy: (boolean) => void
  readTerms: boolean
  setReadTerms: (boolean) => void
  dataConsent: boolean
  setDataConsent: (boolean) => void
  emailOptIn: boolean
  setEmailOptIn: (boolean) => void
}
